import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Play, Square, ChevronDown } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import type { TTSVoice } from '../../types';
import type { RootState } from '../../store';
import { addToFavorites, removeFromFavorites, setSelectedVoice } from '../../store/slices/voicesSlice';
import { getVoicePreview } from '../../services/preview';
import { useAudioPlayer } from '../../hooks/useAudioPlayer';

interface VoiceCardProps {
  voice: TTSVoice;
  showFavorite?: boolean;
}

const VoiceCard: React.FC<VoiceCardProps> = ({ voice, showFavorite = true }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const favorites = useSelector((state: RootState) => state.voices.favorites);
  const isFavorite = favorites.includes(voice.id);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string | undefined>(voice.styles?.[0]);
  const [isStylesOpen, setIsStylesOpen] = useState(false);
  const { isPlaying, loading, play, stop } = useAudioPlayer({ maxDuration: 30 });

  const handleSelect = () => {
    if (!user) {
      navigate('/login', { state: { from: '/tts' } });
      return;
    }

    dispatch(setSelectedVoice({ ...voice, style: selectedStyle || undefined }));
    navigate('/tts');
  };

  const toggleFavorite = () => {
    if (!user) {
      navigate('/login', { state: { from: '/voices' } });
      return;
    }

    if (isFavorite) {
      dispatch(removeFromFavorites({ voiceId: voice.id, userId: user.uid }));
    } else {
      dispatch(addToFavorites({ voiceId: voice.id, userId: user.uid }));
    }
  };

  const handlePreview = async () => {
    try {
      if (isPlaying) {
        stop();
        return;
      }

      setPreviewError(null);
      const { url, error: previewError } = await getVoicePreview(
        { ...voice, style: selectedStyle || undefined },
        user?.uid
      );
      
      if (previewError) {
        setPreviewError(previewError);
        return;
      }

      if (!url) {
        setPreviewError('No preview available');
        return;
      }

      await play(url);
    } catch (err: any) {
      setPreviewError(err.message || 'Failed to play preview');
    }
  };

  const getQualityBadge = () => {
    if (voice.provider === 'azure' && voice.quality === 'hd') {
      return {
        text: 'HD Premium',
        classes: 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white',
        glow: 'shadow-lg shadow-purple-500/20'
      };
    }

    switch (voice.quality) {
      case 'premium':
        return {
          text: 'Premium',
          classes: 'bg-gradient-to-r from-amber-500 to-orange-600 text-white',
          glow: 'shadow-lg shadow-amber-500/20'
        };
      case 'hd':
        return {
          text: 'HD',
          classes: 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white',
          glow: 'shadow-lg shadow-blue-500/20'
        };
      default:
        return {
          text: 'Free',
          classes: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400',
          glow: ''
        };
    }
  };

  const badge = getQualityBadge();
  
  // Determine card styling based on voice quality
  const getCardStyle = () => {
    if (voice.provider === 'azure' && voice.quality === 'hd') {
      return 'bg-gradient-to-br from-purple-900/5 to-indigo-900/5 dark:from-purple-900/20 dark:to-indigo-900/20 border-purple-200 dark:border-purple-900/30 shadow-md';
    }
    
    switch (voice.quality) {
      case 'premium':
        return 'bg-gradient-to-br from-amber-900/5 to-orange-900/5 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-900/30 shadow-md';
      case 'hd':
        return 'bg-gradient-to-br from-blue-900/5 to-cyan-900/5 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200 dark:border-blue-900/30 shadow-md';
      default:
        return 'bg-white dark:bg-dark-800 border-gray-200 dark:border-dark-700 shadow-sm';
    }
  };

  return (
    <div className={`rounded-xl p-4 transition-all duration-300 hover:shadow-lg ${getCardStyle()}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-1">
          {voice.name}
        </h3>
        <div className="flex items-center gap-2">
          {showFavorite && (
            <button
              onClick={toggleFavorite}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700"
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Heart
                className={`w-5 h-5 ${
                  isFavorite
                    ? 'fill-primary-600 text-primary-600'
                    : 'text-gray-400 hover:text-primary-600'
                }`}
              />
            </button>
          )}
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${badge.classes} ${badge.glow}`}>
            {badge.text}
          </span>
        </div>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1 mb-2">
        {voice.accent} • {voice.gender}
      </p>

      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
        {voice.previewText}
      </p>

      {voice.provider === 'azure' && voice.styles && voice.styles.length > 0 && (
        <div className="mb-4">
          <div className="relative">
            <button
              onClick={() => setIsStylesOpen(!isStylesOpen)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 dark:border-dark-700 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
            >
              <span className="capitalize">{selectedStyle || 'Select style'}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isStylesOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isStylesOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg shadow-lg">
                {voice.styles.map((style) => (
                  <button
                    key={style}
                    onClick={() => {
                      setSelectedStyle(style);
                      setIsStylesOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-sm text-left capitalize hover:bg-gray-50 dark:hover:bg-dark-700 ${
                      style === selectedStyle ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600' : ''
                    }`}
                  >
                    {style}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-auto">
        <button
          onClick={handlePreview}
          disabled={loading}
          className="flex items-center px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
        >
          {isPlaying ? (
            <>
              <Square className="w-4 h-4 mr-1" />
              Stop
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-1" />
              Preview
            </>
          )}
        </button>
        <button
          onClick={handleSelect}
          className="px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors duration-200"
        >
          Select
        </button>
      </div>

      {previewError && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
          {previewError}
        </p>
      )}
    </div>
  );
};

export default VoiceCard;